/**
 * Global event system for calendar refresh notifications
 * This allows any part of the app to trigger calendar updates
 */

export type CalendarEventType = 
  | 'player-added'
  | 'player-updated' 
  | 'player-deleted'
  | 'player-assigned'
  | 'player-unassigned'
  | 'team-created'
  | 'team-updated'
  | 'team-deleted'
  | 'league-created'
  | 'league-updated'
  | 'league-deleted'
  | 'league-synced'
  | 'schedule-imported'
  | 'schedule-synced'
  | 'match-created'
  | 'match-updated'
  | 'match-deleted'
  | 'data-refresh-needed';

export interface CalendarEvent {
  type: CalendarEventType;
  data?: any;
  timestamp: number;
}

class CalendarEventEmitter {
  private listeners: Map<CalendarEventType | 'all', Set<(event: CalendarEvent) => void>> = new Map();

  /**
   * Subscribe to calendar events
   */
  on(eventType: CalendarEventType | 'all', callback: (event: CalendarEvent) => void) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType)!.add(callback);

    // Return unsubscribe function
    return () => {
      this.listeners.get(eventType)?.delete(callback);
    };
  }

  /**
   * Emit a calendar event
   */
  emit(type: CalendarEventType, data?: any) {
    const event: CalendarEvent = {
      type,
      data,
      timestamp: Date.now()
    };

    // Notify specific event listeners
    this.listeners.get(type)?.forEach(callback => {
      try {
        callback(event);
      } catch (error) {
        console.error('Error in calendar event listener:', error);
      }
    });

    // Notify 'all' event listeners
    this.listeners.get('all')?.forEach(callback => {
      try {
        callback(event);
      } catch (error) {
        console.error('Error in calendar event listener:', error);
      }
    });
  }

  /**
   * Remove all listeners for an event type
   */
  removeAllListeners(eventType?: CalendarEventType | 'all') {
    if (eventType) {
      this.listeners.delete(eventType);
    } else {
      this.listeners.clear();
    }
  }
}

// Global instance
export const calendarEvents = new CalendarEventEmitter();

// Convenience functions for common events
export const emitPlayerChange = (type: 'added' | 'updated' | 'deleted' | 'assigned' | 'unassigned', data?: any) => {
  calendarEvents.emit(`player-${type}` as CalendarEventType, data);
};

export const emitTeamChange = (type: 'created' | 'updated' | 'deleted', data?: any) => {
  calendarEvents.emit(`team-${type}` as CalendarEventType, data);
};

export const emitLeagueChange = (type: 'created' | 'updated' | 'deleted' | 'synced', data?: any) => {
  calendarEvents.emit(`league-${type}` as CalendarEventType, data);
};

export const emitScheduleChange = (type: 'imported' | 'synced', data?: any) => {
  calendarEvents.emit(`schedule-${type}` as CalendarEventType, data);
};

export const emitMatchChange = (type: 'created' | 'updated' | 'deleted', data?: any) => {
  calendarEvents.emit(`match-${type}` as CalendarEventType, data);
};

export const emitDataRefreshNeeded = (reason?: string) => {
  calendarEvents.emit('data-refresh-needed', { reason });
};
